import React, { createContext, useContext, useState, useEffect } from "react";
import { ACCOUNT_AUTH_LOSS_REDIRECT_KEY } from "@/utils/authLossRedirect";
import { resolveUserAvatarSrc } from "@/utils/userAvatar";

// Shape of the guest user
export interface GuestUser {
    id: string;
    name: string;
    email: string;
    avatar: string;
    isGuest: true;
}

// Shape of the context value
interface GuestUserContextType {
    guestUser: GuestUser | null;
    setGuestUser: (user: Omit<GuestUser, "avatar" | "isGuest"> | null) => void;
    clearGuestUser: () => void;
}

// Create context
const GuestUserContext = createContext<GuestUserContextType | undefined>(undefined);

const generateGuestAvatar = (name: string) =>
    resolveUserAvatarSrc({
        name,
        userId: `guest:${name}`,
    });

const getStoredGuestUser = (): GuestUser | null => {
    if (typeof window === "undefined") return null;

    try {
        const storedGuestUser = localStorage.getItem("guestUser");
        if (!storedGuestUser) return null;

        const parsed = JSON.parse(storedGuestUser);
        const name = parsed?.name || "Guest";

        return {
            ...parsed,
            name,
            email: parsed?.email || "guest@qit.app",
            avatar: parsed?.avatar || generateGuestAvatar(name),
            isGuest: true,
        };
    } catch {
        try {
            localStorage.removeItem("guestUser");
        } catch {
            // Storage can be unavailable in restricted TV browser modes.
        }
        return null;
    }
};

export const GuestUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [guestUser, setGuestUserState] = useState<GuestUser | null>(() => getStoredGuestUser());

    // Load from storage on mount
    useEffect(() => {
        setGuestUserState(getStoredGuestUser());
    }, []);

    // Handle setting guest user with auto-avatar
    const setGuestUser = (user: Omit<GuestUser, "avatar" | "isGuest"> | null) => {
        if (user) {
            const fullUser: GuestUser = {
                id: user.id,
                name: user.name || "Guest",
                email: user.email || "guest@qit.app",
                avatar: generateGuestAvatar(user.name || "Guest"),
                isGuest: true,
            };
            setGuestUserState(fullUser);
            sessionStorage.removeItem(ACCOUNT_AUTH_LOSS_REDIRECT_KEY);
            localStorage.setItem("guestUser", JSON.stringify(fullUser));
        } else {
            setGuestUserState(null);
            localStorage.removeItem("guestUser");
        }
    };

    const clearGuestUser = () => {
        setGuestUserState(null);
        localStorage.removeItem("guestUser");
    };

    return (
        <GuestUserContext.Provider value={{ guestUser, setGuestUser, clearGuestUser }}>
            {children}
        </GuestUserContext.Provider>
    );
};

// Hook to use the context
export const useGuestUser = (): GuestUserContextType => {
    const context = useContext(GuestUserContext);
    if (!context) {
        throw new Error("useGuestUser must be used within a GuestUserProvider");
    }
    return context;
};
